import { useState, useCallback } from 'react';

/**
 * Custom hook for document generation output state.
 * Extracted from App.js — isGenerating, result, output tabs, etc.
 */
export const useDocumentOutput = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [documentsReady, setDocumentsReady] = useState(false);
  const [generationVersion, setGenerationVersionRaw] = useState(() => {
    const saved = localStorage.getItem('writeright_generation_version');
    const parsed = saved ? parseInt(saved, 10) : 0;
    return isNaN(parsed) ? 0 : parsed;
  });
  
  // Persist version counter so it survives page reload / session restore
  const setGenerationVersion = useCallback((valOrFn) => {
    setGenerationVersionRaw(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      localStorage.setItem('writeright_generation_version', String(next));
      return next;
    });
  }, []);
  const [copiedField, setCopiedField] = useState(null);
  const [activeOutputTab, setActiveOutputTab] = useState('indication');
  const [lastShareUrl, setLastShareUrl] = useState(null);

  // Bracket modal state (for filling in [PLACEHOLDER] fields)
  // Feb 24, 2026: bracketFields is persisted to localStorage so date / rank /
  // unit / SSN / DOB answers survive refresh + regen. Previously the user
  // had to re-fill the same brackets on every page reload. We persist on
  // every change via a wrapper setter.
  const [showBracketModal, setShowBracketModal] = useState(false);
  const [bracketFields, setBracketFieldsRaw] = useState(() => {
    try {
      const raw = localStorage.getItem('writeright_bracket_fields');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const setBracketFields = useCallback((valOrFn) => {
    setBracketFieldsRaw(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        if (next && typeof next === 'object' && Object.keys(next).length > 0) {
          localStorage.setItem('writeright_bracket_fields', JSON.stringify(next));
        } else {
          localStorage.removeItem('writeright_bracket_fields');
        }
      } catch { /* ignore */ }
      return next;
    });
  }, []);
  const [pendingGeneration, setPendingGeneration] = useState(null);
  
  // Pre-generation modal
  const [showPreGenModal, setShowPreGenModal] = useState(false);
  const [preGenFields, setPreGenFields] = useState({});

  // Conflict modal (Feb 26, 2026) — pre-generate scan for pronoun mix and
  // endorse/denies contradictions. See `features/conflict-scanner/scanner.js`.
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  const clearResults = useCallback(() => {
    setResult(null);
    setDocumentsReady(false);
    setActiveOutputTab('indication');
    setCopiedField(null);
    setLastShareUrl(null);
    // Reset version counter for new patient
    setGenerationVersion(0);
  }, [setGenerationVersion]);

  return {
    isGenerating, setIsGenerating,
    result, setResult,
    documentsReady, setDocumentsReady,
    generationVersion, setGenerationVersion,
    copiedField, setCopiedField,
    activeOutputTab, setActiveOutputTab,
    lastShareUrl, setLastShareUrl,
    showBracketModal, setShowBracketModal,
    bracketFields, setBracketFields,
    pendingGeneration, setPendingGeneration,
    showPreGenModal, setShowPreGenModal,
    preGenFields, setPreGenFields,
    showConflictModal, setShowConflictModal,
    conflicts, setConflicts,
    clearResults,
  };
};

export default useDocumentOutput;
